import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Grid,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  TextField,
} from "@mui/material";
import ProductCreate from "pages/products/ProductCreate";
import ProductImages from "pages/products/ProductImages";
import ProductVideos from "pages/products/ProductVideos";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearProduct,
  refreshCurrentProductThunk,
} from "store/reducers/product/productSlice";
import ProductGstCreate from "./ProductGstCreate";
import moment from "moment";
import { dispatch } from "store";
import { createProductVariantThunk } from "store/reducers/product/productVariantSlice";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

const ProductCreateWizard = () => {
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = React.useState(0);
  const [imageUploaded, setImageUploaded] = React.useState(false);
  const [addedPrice, setAddedPrice] = React.useState(false);
  const [videoUploaded, setVideoUploaded] = React.useState(false);
  const [gst, setGst] = React.useState(false);
  const [priceVariant, setPriceVariant] = React.useState(false);

  const refreshProduct = () => {
    dispatch(refreshCurrentProductThunk(currentProduct.id));
  };

  const imageStepCompleted = () => {
    refreshProduct();
    setImageUploaded(true);
  };

  const videoStepCompleted = () => {
    setVideoUploaded(true);
  };

  const priceVariantCompleted = () => {
    setPriceVariant(true);
    handleNext();
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  useEffect(() => {
    dispatch(clearProduct());
    return () => {
      dispatch(clearProduct());
    };
  }, [dispatch]);

  const currentProduct = useSelector((state) => state.product.currentProduct);
  const isSmallSCreen = useMediaQuery((theme) => theme.breakpoints.down("md"));

  return (
    <Box>
      <Typography variant="h5" color="gray">
        Welcome to product creation wizard
      </Typography>
      <Stepper
        activeStep={activeStep}
        orientation="vertical"
        sx={{ marginTop: 1 }}
      >
        <Step>
          <StepLabel>
            <Typography variant="h6">Add basic details</Typography>
          </StepLabel>
          <StepContent>
            <ProductCreate handleNext={handleNext} />
          </StepContent>
        </Step>

        <Step completed={priceVariant}>
          <StepLabel>
            <Typography variant="h6">Add Price Variants</Typography>
          </StepLabel>
          <StepContent>
            <Grid container>
              <Grid item xs={12} md={6} lg={6}>
                <PriceVariantForm
                  onComplete={priceVariantCompleted}
                  product_id={currentProduct?.id}
                />
              </Grid>
            </Grid>
          </StepContent>
        </Step>

        <Step completed={imageUploaded}>
          <StepLabel>
            <Typography variant="h6">Add Images</Typography>
          </StepLabel>
          <StepContent>
            <Grid container columnSpacing={2} rowSpacing={0}>
              <Grid item xs={12} md={6} lg={6}>
                <ProductImages
                  product={currentProduct}
                  fullGrid={true}
                  completed={imageStepCompleted}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row-reverse",
                    alignItems: "center",
                    marginTop: 3,
                  }}
                >
                  {imageUploaded && (
                    <LoadingButton
                      variant="contained"
                      fullWidth={isSmallSCreen}
                      type="submit"
                      onClick={handleNext}
                    >
                      Continue
                    </LoadingButton>
                  )}

                  <Button color="inherit" onClick={handleSkip}>
                    Skip
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </StepContent>
        </Step>

        <Step completed={videoUploaded}>
          <StepLabel>
            <Typography variant="h6">Add Videos</Typography>
          </StepLabel>
          <StepContent>
            <Grid container columnSpacing={2} rowSpacing={0}>
              <Grid item xs={12} md={6} lg={6}>
                <ProductVideos
                  product={currentProduct}
                  fullGrid={true}
                  completed={videoStepCompleted}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row-reverse",
                    alignItems: "center",
                    marginTop: 3,
                  }}
                >
                  {imageUploaded && (
                    <LoadingButton
                      variant="contained"
                      fullWidth={isSmallSCreen}
                      type="submit"
                      onClick={handleNext}
                    >
                      Continue
                    </LoadingButton>
                  )}

                  <Button color="inherit" onClick={handleSkip}>
                    Skip
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </StepContent>
        </Step>

        <Step completed={gst}>
          <StepLabel>
            <Typography variant="h6">Add Gst details</Typography>
          </StepLabel>
          <StepContent>
            <Grid container columnSpacing={2} rowSpacing={0}>
              <Grid item xs={12} md={6} lg={6}>
                <ProductGstCreate
                  handleNext={handleNext}
                  product={currentProduct?.id}
                />
              </Grid>
            </Grid>
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );
};

// Inline form referencing ProductVariantForm
function PriceVariantForm({ onComplete, product_id }) {
  const [price, setPrice] = React.useState(0);
  const [expiryDate, setExpiryDate] = React.useState(
    moment().add(10, "days").format("YYYY-MM-DD")
  );
  const { success } = useSelector((state) => state.productVariant);

  const handleSubmit = () => {
    dispatch(
      createProductVariantThunk({
        price,
        expiry_date: expiryDate,
        product: product_id,
      })
    );
    onComplete();
  };

  return (
    <Box>
      <TextField
        label="Price"
        type="number"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        fullWidth
      />
      <LocalizationProvider dateAdapter={AdapterMoment} sx={{ marginTop: 2 }}>
        <DesktopDatePicker
          label="Expiry date"
          inputFormat="YYYY-MM-DD"
          name="expiry_date"
          value={expiryDate}
          onChange={(value) => setExpiryDate(value.format("YYYY-MM-DD"))}
          renderInput={(params) => (
            <TextField
              fullWidth
              variant="outlined"
              {...params}
              sx={{ marginTop: 2 }}
            />
          )}
        />
      </LocalizationProvider>
      <LoadingButton
        variant="contained"
        onClick={handleSubmit}
        sx={{ marginTop: 2 }}
      >
        Continue
      </LoadingButton>
    </Box>
  );
}

export default ProductCreateWizard;
